// TMOS Public Website Header Component
// Date: January 27, 2025
// Purpose: Header navigation for public website pages

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Modal } from '@/components/ui/Modal';
import { LoginForm } from '@/components/public/LoginForm';
import publicApi from '@/services/publicApi';
import tmosLogo from '@/assets/images/brand/tmos-logo.png';

interface PublicHeaderProps {
    onLoginClick?: () => void;
}

interface NavigationItem {
    item_key: string;
    label: string;
    path: string;
    is_active: boolean;
}

export const PublicHeader: React.FC<PublicHeaderProps> = ({ onLoginClick }) => {
    const location = useLocation();
    const { isAuthenticated } = useAuth();
    const { currentLanguage, setLanguage, availableLanguages, translateWithFallback } = useLanguage();
    const tf = (key: string, fallback: string) => translateWithFallback(key, fallback);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
    const [navigationItems, setNavigationItems] = useState<NavigationItem[]>([]);
    const [loginModalOpen, setLoginModalOpen] = useState(false);

    useEffect(() => {
        loadNavigationItems();
    }, []);

    const loadNavigationItems = async () => {
        try {
            const response = await publicApi.get('/public/navigation-items');
            if (response.data.success) {
                setNavigationItems(response.data.data || []);
            }
        } catch (error) {
            console.error('Error loading navigation items:', error);
            // Fallback to default navigation if API fails
            // Why T-MOS is merged into home; do not surface it in fallback nav
            setNavigationItems([
                { item_key: 'product', label: tf('website.nav.product', 'Product'), path: '/product', is_active: true },
                { item_key: 'solutions', label: tf('website.nav.solutions', 'Solutions'), path: '/solutions', is_active: true },
                { item_key: 'marketplace', label: tf('website.nav.marketplace', 'Marketplace'), path: '/marketplace', is_active: true },
            ]);
        }
    };

    const isActive = (path: string) => location.pathname === path;

    // Filter to only active items; Why T-MOS merged into home; Pricing is post-login only
    const navigation = navigationItems
        .filter(
            (item) =>
                item.is_active &&
                item.path !== '/why-tmos' &&
                item.item_key !== 'why_tmos' &&
                item.path !== '/pricing'
        )
        .map((item) => ({
            name: item.label,
            path: item.path,
        }));

    const handleLanguageChange = (languageId: string) => {
        setLanguage(languageId);
        setLanguageDropdownOpen(false);
    };

    return (
        <header className="bg-[var(--color-surface-bg)] shadow-sm border-b border-[var(--color-border)]">
            <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Top">
                <div className="flex w-full items-center justify-between py-4">
                    {/* Logo */}
                    <div className="flex items-center">
                        <Link to="/" className="flex items-center">
                            <img
                                src={tmosLogo}
                                alt="T-MOS logo"
                                className="h-10 w-auto"
                            />
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex md:items-center md:space-x-8">
                        {navigation.map((item) => (
                            <Link
                                key={item.name}
                                to={item.path}
                                className={`text-base font-medium transition-colors ${
                                    isActive(item.path)
                                        ? 'text-[var(--color-primary-text)] border-b-2 border-[var(--color-primary-text)] pb-1'
                                        : 'text-[var(--color-surface-text)] hover:text-[var(--color-primary-text)]'
                                }`}
                            >
                                {item.name}
                            </Link>
                        ))}
                    </div>

                    {/* Language Selector, Login and Request Demo Buttons (Legal lives in page/login footer) */}
                    <div className="hidden md:flex md:items-center md:space-x-3">
                        {/* Language Selector */}
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                                className="flex items-center space-x-1.5 px-3 py-2 text-sm font-medium text-[var(--color-surface-text)] hover:text-[var(--color-primary-text)] transition-colors border border-[var(--color-border)] rounded-md hover:border-[var(--color-primary-text)] bg-[var(--color-surface-bg)]"
                                title="Select Language"
                            >
                                <span>{currentLanguage?.flag || '🌐'}</span>
                                <span className="hidden lg:inline">{currentLanguage?.nativeName || currentLanguage?.id || 'English'}</span>
                                <ChevronDownIcon className={`h-4 w-4 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {languageDropdownOpen && (
                                <>
                                    <div 
                                        className="fixed inset-0 z-10" 
                                        onClick={() => setLanguageDropdownOpen(false)}
                                    />
                                    <div className="absolute right-0 mt-2 w-48 bg-[var(--color-surface-bg)] rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-20 border border-[var(--color-border)]">
                                        <div className="py-1" role="menu">
                                            {(availableLanguages || []).map((lang) => (
                                                <button
                                                    key={lang.id}
                                                    onClick={() => handleLanguageChange(lang.id)}
                                                    className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 hover:bg-[var(--color-secondary-bg)] transition-colors ${
                                                        currentLanguage?.id === lang.id
                                                          ? 'bg-[var(--color-primary-text)] text-[var(--color-primary-bg)]'
                                                          : 'text-[var(--color-surface-text)]'
                                                    }`}
                                                    role="menuitem"
                                                >
                                                    <span>{lang.flag}</span>
                                                    <span>{lang.nativeName}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        {isAuthenticated ? (
                            <Link
                                to="/dashboard"
                                className="rounded-lg bg-[var(--color-primary-text)] px-6 py-2.5 text-sm font-semibold text-[var(--color-primary-bg)] shadow-sm hover:opacity-90 transition-opacity"
                            >
                                {tf('website.nav.go_to_dashboard', 'Go to Dashboard')}
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/request-demo"
                                    className="rounded-lg border-2 border-[var(--color-primary-text)] px-6 py-2.5 text-sm font-semibold text-[var(--color-primary-text)] hover:bg-[var(--color-primary-text)] hover:text-[var(--color-primary-bg)] transition-colors"
                                >
                                    {tf('website.nav.request_demo', 'Request a Demo')}
                                </Link>
                                <button
                                    onClick={() => {
                                        if (onLoginClick) {
                                            // Homepage handles its own modal
                                            onLoginClick();
                                        } else {
                                            // Other pages use header's modal
                                            setLoginModalOpen(true);
                                        }
                                    }}
                                    className="rounded-lg bg-[var(--color-secondary-bg)] px-6 py-2.5 text-sm font-semibold text-[var(--color-surface-text)] shadow-sm border border-[var(--color-border)] hover:border-[var(--color-primary-text)] transition-colors"
                                >
                                    {tf('website.nav.login', 'Login')}
                                </button>
                            </>
                        )}
                    </div>

                    {/* Mobile menu button */}
                    <div className="md:hidden">
                        <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-md p-2 text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-primary-text)]"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            <span className="sr-only">Open main menu</span>
                            {mobileMenuOpen ? (
                                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            ) : (
                                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-[var(--color-border)] py-4">
                        <div className="space-y-2">
                            {navigation.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`block px-3 py-2 text-base font-medium rounded-md ${
                                        isActive(item.path)
                                            ? 'bg-[var(--color-primary-text)] text-[var(--color-primary-bg)]'
                                            : 'text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]'
                                    }`}
                                >
                                    {item.name}
                                </Link>
                            ))}
                            <div className="pt-4 border-t border-[var(--color-border)] space-y-2">
                                {isAuthenticated ? (
                                    <Link
                                        to="/dashboard"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="block w-full rounded-lg bg-[var(--color-primary-text)] px-4 py-2.5 text-center text-sm font-semibold text-[var(--color-primary-bg)] shadow-sm hover:opacity-90 transition-opacity"
                                    >
                                        {tf('website.nav.go_to_dashboard', 'Go to Dashboard')}
                                    </Link>
                                ) : (
                                    <>
                                        <Link
                                            to="/request-demo"
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="block w-full rounded-lg border-2 border-[var(--color-primary-text)] px-4 py-2.5 text-center text-sm font-semibold text-[var(--color-primary-text)] hover:bg-[var(--color-primary-text)] hover:text-[var(--color-primary-bg)] transition-colors"
                                        >
                                            {tf('website.nav.request_demo', 'Request a Demo')}
                                        </Link>
                                        <button
                                            onClick={() => {
                                                setMobileMenuOpen(false);
                                                setLoginModalOpen(true);
                                            }}
                                            className="block w-full rounded-lg bg-[var(--color-secondary-bg)] px-4 py-2.5 text-center text-sm font-semibold text-[var(--color-surface-text)] shadow-sm border border-[var(--color-border)] hover:border-[var(--color-primary-text)] transition-colors"
                                        >
                                            {tf('website.nav.login', 'Login')}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </nav>

            {/* Login Modal */}
            <Modal
                isOpen={loginModalOpen}
                onClose={() => setLoginModalOpen(false)}
                title={tf('website.home.sign_in.title', 'Sign In')}
                size="sm"
                className="bg-[var(--color-surface-bg)]"
            >
                <div className="space-y-4">
                    <LoginForm
                        onSuccess={() => {
                            setLoginModalOpen(false);
                        }}
                    />
                    <div className="text-center pt-4 border-t border-[var(--color-border)]">
                        <Link
                            to="/register"
                            onClick={() => setLoginModalOpen(false)}
                            className="text-base text-[var(--color-surface-text)] font-bold hover:text-[var(--color-primary-text)] underline transition-colors inline-block"
                        >
                            Or Create a new account
                        </Link>
                    </div>
                </div>
            </Modal>
        </header>
    );
};

